// App — go-pim-ui shell.
//
// Sidebar + main column + toast portal + dev StrataTweaks. The router's
// <Outlet /> renders the active page inside <main>. go-pim has a single nav
// list (no merchant switcher / status banner / admin-vs-merchant branching —
// those were deleted from the strata fork).
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Avatar, ToastStack } from "@/components/UI";
import { StrataTweaks } from "@/components/StrataTweaks";
import { useAuth } from "@/store/auth";
import { useUI } from "@/store/ui";
import { useMe } from "@/queries/useMe";
import { hexToOklch } from "@/utils/color";

interface NavItem {
  key: string;
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
}

const CRUMB_MAP: Record<string, string> = {
  "/": "Companies",
  "/products": "Products",
  "/documents": "Documents",
  "/email-templates": "Email templates",
  "/settings": "Settings",
};

const NAV_ITEMS: NavItem[] = [
  { key: "companies", to: "/", label: "Companies", icon: I.Building },
  { key: "products", to: "/products", label: "Products", icon: I.Wallet },
  { key: "documents", to: "/documents", label: "Documents", icon: I.ClipboardCheck },
  { key: "email", to: "/email-templates", label: "Email", icon: I.Mail },
  { key: "settings", to: "/settings", label: "Settings", icon: I.Settings },
];

export function App() {
  useMe(); // hydrate user store
  const location = useLocation();
  const navigate = useNavigate();

  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const qc = useQueryClient();
  const theme = useUI((s) => s.theme);
  const density = useUI((s) => s.density);
  const accent = useUI((s) => s.accent);
  const toasts = useUI((s) => s.toasts);

  // Apply tokens to <html> on first render and whenever ui state changes.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.density = density;
    document.documentElement.style.setProperty("--accent", hexToOklch(accent));
  }, [theme, density, accent]);

  // Crumb for a bare path; company detail / products fall through to "Detail".
  const crumb = CRUMB_MAP[location.pathname] ?? "Detail";
  const displayName = user?.email?.split("@")[0] ?? "Operator";
  const roleLabel = user?.role ?? "Operator";

  return (
    <>
      <div className="app" data-screen-label={"console-" + location.pathname.slice(1)}>
        <aside className="sidebar">
          <div className="brand">
            <img
              src="/pim-logo.png"
              alt=""
              className="brand-mark"
              aria-hidden="true"
            />
            <span className="brand-name">GO·PIM</span>
            <span className="brand-env">Operator</span>
          </div>

          <div className="nav-section">Manage</div>
          <nav className="stack-2">
            {NAV_ITEMS.map((it) => (
              <NavItemLink key={it.key} item={it} />
            ))}
          </nav>

          <div className="sidebar-foot">
            <div className="user-chip">
              <Avatar name={displayName} size={28} />
              <div className="meta">
                <div className="n">{displayName}</div>
                <div className="r">{roleLabel}</div>
              </div>
              <button
                className="icon-btn"
                type="button"
                title="Sign out"
                aria-label="Sign out"
                onClick={async () => {
                  await logout();
                  // Drop every cached query so the next login (potentially a
                  // different user) cannot see the previous account's data
                  // between mount and refetch.
                  qc.clear();
                  navigate("/login", { replace: true });
                }}
              >
                <I.ArrowRight size={12} />
              </button>
            </div>
          </div>
        </aside>

        <main className="main">
          <header className="header">
            <div className="crumbs">
              <span>Console</span>
              <span className="sep">/</span>
              <span className="here">{crumb}</span>
            </div>
            <div className="spacer" />
          </header>

          <Outlet />
        </main>
      </div>

      {import.meta.env.DEV && <StrataTweaks />}
      <ToastStack items={toasts} />
    </>
  );
}

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
    >
      <item.icon />
      <span>{item.label}</span>
    </NavLink>
  );
}
